/**
 * observationTaskAdministration.js
 *
 * Single source of truth for the per-task "Not administered" flag in the Adult
 * Autism Behavioural Observation (OBS) module.
 *
 * Business context
 * ----------------
 * A clinician does not always run every task they selected for an assessment
 * (time, patient tolerance, clinical judgement). Rather than deleting such a
 * task — which would lose any notes already jotted against it — they mark it
 * "Not administered". The task stays in the assessment (still visible, still in
 * `selectedTaskIds`) but is excluded from:
 *
 *   - Task Administration / Criterion Mapping completeness and progress maths
 *   - the criteria-generation gate and the "incomplete mapping" warning list
 *   - the evidence tables on the OBS Review and MDT Review pages
 *   - the LLM inputs used to draft the Criteria paragraphs
 *
 * The flag is stored non-destructively at
 * `data.allTaskData.<taskId>.notAdministered` and is reversible: unmarking a
 * task restores every note and decision exactly as it was.
 *
 * Why a dedicated leaf module
 * ---------------------------
 * Several unrelated readers need the exclusion rule (routing/progress helpers,
 * criteria-completion helpers, the note-move target builder). Putting the
 * predicate here — rather than in `observationCriteriaCompletion.js` — keeps
 * those helpers importing a single small primitive and avoids a dependency
 * cycle (`autismObsRouting.js` already imports the criteria-completion module).
 *
 * IMPORTANT: every reader must use a *truthy* check on `notAdministered`.
 * Unmarking writes `false` (rather than deleting the field), and existing
 * patients have no such field at all, so `!taskData?.notAdministered` treats
 * both "absent" and "false" as administered.
 */

/**
 * True when a task should be counted as administered (i.e. NOT flagged as
 * "not administered"). Missing task data counts as administered — a task the
 * clinician has not touched yet is assumed to be part of the assessment.
 *
 * @param {Object} [taskData] - The per-task blob (`allTaskData[taskId]`)
 * @returns {boolean}
 */
export function isObservationTaskAdministered(taskData) {
  return !taskData?.notAdministered;
}

/**
 * Filters a list of selected task ids down to only those that were actually
 * administered. Use this anywhere the *set* of contributing tasks matters but
 * their positional display number does NOT — completeness maths, progress
 * fractions, the criteria-generation gate.
 *
 * Do NOT use this where a task's display number is derived from its index in
 * `selectedTaskIds` (evidence tables, the incomplete-mapping warning list):
 * dropping earlier entries would silently renumber the tasks after them. In
 * those places, attach the number from the original index first, then filter.
 *
 * @param {Array<number|string>} selectedTaskIds
 * @param {Object} [allTaskData] - Per-task data keyed by task id
 * @returns {Array<number|string>}
 */
export function getAdministeredTaskIds(selectedTaskIds, allTaskData) {
  if (!Array.isArray(selectedTaskIds)) return [];
  return selectedTaskIds.filter(
    (id) => isObservationTaskAdministered(allTaskData?.[id]),
  );
}

/**
 * True when a task already holds clinician-entered content: at least one
 * general-observation note with real text, any recorded evidence decision, or
 * any criterion-mapped note with real text.
 *
 * This powers the confirmation prompt shown before marking a task "Not
 * administered" — we only interrupt the clinician when they would be excluding
 * content they have actually recorded. An untouched task toggles in one click.
 *
 * Only inspects fields the OBS panels actually write:
 *   - masterNotes:            general observation notes
 *   - dsm_<id>_evidence:      per-criterion evidence decision
 *   - dsm_<id>_notes:         per-criterion mapped notes
 *
 * @param {Object} [taskData] - The per-task blob (`allTaskData[taskId]`)
 * @returns {boolean}
 */
export function observationTaskHasRecordedData(taskData) {
  if (!taskData || typeof taskData !== 'object') return false;

  const hasNoteText = (notes) =>
    Array.isArray(notes) && notes.some((n) => n && String(n.text || '').trim());

  // Any general observation note with real text.
  if (hasNoteText(taskData.masterNotes)) return true;

  // Any evidence decision, or any criterion-mapped note with real text.
  for (const key of Object.keys(taskData)) {
    if (/^dsm_.+_evidence$/.test(key) && taskData[key]) return true;
    if (/^dsm_.+_notes$/.test(key) && hasNoteText(taskData[key])) return true;
  }

  return false;
}
